import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Box, Calculator, CheckCircle2, Columns3, Layers, Plus, Route, Shapes, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatProductPrice, getProducts } from '../services/productCatalog';

const JOBS = {
  dak: { label: 'Dak / Pelat', labelEn: 'Slab', waste: 0.03, strength: 'K250', icon: Layers },
  jalan: { label: 'Jalan', labelEn: 'Road', waste: 0.05, strength: 'K300', icon: Route },
  pondasi: { label: 'Pondasi', labelEn: 'Foundation', waste: 0.07, strength: 'K225', icon: Box },
  kolom: { label: 'Kolom', labelEn: 'Column', waste: 0.05, strength: 'K300', icon: Columns3 },
  lainnya: { label: 'Lainnya', labelEn: 'Other', waste: 0.10, strength: 'K250', icon: Shapes }
};

export default function HomeCalculator() {
  const { lang } = useLanguage();
  const { addToCart } = useCart();
  const [job, setJob] = useState('dak');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [thickness, setThickness] = useState('');
  const [products, setProducts] = useState([]);
  const [notice, setNotice] = useState('');

  const copy = lang === 'en' ? {
    eyebrow: 'CONCRETE VOLUME CALCULATOR',
    title: 'Estimate Your Concrete Requirements',
    lead: 'Enter the project dimensions to estimate volume, recommended concrete grade, and delivery fleet.',
    project: 'Project type', length: 'Length', width: 'Width', thickness: 'Thickness',
    empty: 'Enter complete dimensions to view the calculation.', needed: 'Estimated concrete required',
    base: 'Base volume', waste: 'Waste allowance', grade: 'Recommended grade', fleet: 'Fleet estimate',
    standard: 'standard trucks', minimix: 'minimix trucks', add: 'Add Recommendation to Cart', catalog: 'View Product Catalog',
    disclaimer: 'This is an initial estimate. Final requirements must be confirmed against site conditions and technical review.',
    added: 'The calculation recommendation has been added to your cart.', unavailable: 'The recommended grade is not active in the product master.'
  } : {
    eyebrow: 'KALKULATOR VOLUME BETON',
    title: 'Hitung Estimasi Kebutuhan Beton',
    lead: 'Masukkan dimensi proyek untuk mendapatkan estimasi volume, rekomendasi mutu beton, dan kebutuhan armada.',
    project: 'Jenis pekerjaan', length: 'Panjang', width: 'Lebar', thickness: 'Ketebalan',
    empty: 'Lengkapi seluruh dimensi untuk melihat hasil perhitungan.', needed: 'Estimasi kebutuhan beton',
    base: 'Volume dasar', waste: 'Cadangan material', grade: 'Rekomendasi mutu', fleet: 'Estimasi armada',
    standard: 'truk standard', minimix: 'truk minimix', add: 'Tambah Rekomendasi ke Keranjang', catalog: 'Lihat Katalog Produk',
    disclaimer: 'Hasil ini merupakan estimasi awal. Kebutuhan final perlu dikonfirmasi berdasarkan kondisi lapangan dan kajian teknis.',
    added: 'Rekomendasi hasil kalkulasi sudah ditambahkan ke keranjang.', unavailable: 'Mutu rekomendasi belum aktif pada master produk.'
  };

  useEffect(() => {
    getProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const result = useMemo(() => {
    const lengthValue = Number(length);
    const widthValue = Number(width);
    const thicknessValue = Number(thickness);
    if (lengthValue <= 0 || widthValue <= 0 || thicknessValue <= 0) return null;

    const selectedJob = JOBS[job];
    const baseVolume = lengthValue * widthValue * (thicknessValue / 100);
    const finalVolume = Math.ceil(baseVolume * (1 + selectedJob.waste) * 10) / 10;
    return {
      baseVolume,
      finalVolume,
      wasteVolume: finalVolume - baseVolume,
      wastePercent: selectedJob.waste * 100,
      strength: selectedJob.strength,
      standardTrucks: Math.ceil(finalVolume / 7),
      minimixTrucks: Math.ceil(finalVolume / 3)
    };
  }, [job, length, width, thickness]);

  const recommendedProduct = result
    ? products.find(product => product.strength_class?.toUpperCase() === result.strength)
    : null;

  const handleAddToCart = () => {
    if (!result || !recommendedProduct) return;
    addToCart(recommendedProduct, result.finalVolume, 'standard', {
      jobType: job,
      calculatorEstimate: true
    });
    setNotice(copy.added);
  };

  return (
    <section id="kalkulator" className="home-calculator-section">
      <div className="container">
        <div className="calculator-heading">
          <span className="eyebrow">{copy.eyebrow}</span>
          <h2 className="section-title">{copy.title}</h2>
          <p>{copy.lead}</p>
        </div>

        <div className="home-calculator-shell">
          <div className="calculator-form-panel">
            <div className="calculator-panel-label"><Calculator size={20} /> {copy.project}</div>
            <div className="calculator-job-grid">
              {Object.entries(JOBS).map(([key, item]) => {
                const Icon = item.icon;
                return (
                  <button type="button" key={key} className={job === key ? 'active' : ''} onClick={() => { setJob(key); setNotice(''); }}>
                    <Icon size={18} />
                    <span>{lang === 'en' ? item.labelEn : item.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="calculator-dimensions">
              <label>
                <span>{copy.length}</span>
                <div><input type="number" min="0" step="0.1" value={length} onChange={event => { setLength(event.target.value); setNotice(''); }} placeholder="0.0" /><b>m</b></div>
              </label>
              <label>
                <span>{copy.width}</span>
                <div><input type="number" min="0" step="0.1" value={width} onChange={event => { setWidth(event.target.value); setNotice(''); }} placeholder="0.0" /><b>m</b></div>
              </label>
              <label>
                <span>{copy.thickness}</span>
                <div><input type="number" min="0" step="1" value={thickness} onChange={event => { setThickness(event.target.value); setNotice(''); }} placeholder="0" /><b>cm</b></div>
              </label>
            </div>

            <div className="calculator-form-note">
              <Plus size={16} />
              <span>{copy.waste}: <strong>{JOBS[job].waste * 100}%</strong></span>
            </div>
          </div>

          <div className={`calculator-result-panel${result ? ' has-result' : ''}`}>
            {!result ? (
              <div className="calculator-empty-state">
                <Calculator size={42} />
                <p>{copy.empty}</p>
              </div>
            ) : (
              <>
                <span className="calculator-result-kicker">{copy.needed}</span>
                <div className="calculator-total"><strong>{result.finalVolume.toFixed(1)}</strong><span>m³</span></div>
                <div className="calculator-result-grid">
                  <div><small>{copy.base}</small><strong>{result.baseVolume.toFixed(2)} m³</strong></div>
                  <div><small>{copy.waste} ({result.wastePercent}%)</small><strong>+{result.wasteVolume.toFixed(2)} m³</strong></div>
                  <div><small>{copy.grade}</small><strong>{result.strength}</strong></div>
                  <div><small>{copy.fleet}</small><strong><Truck size={15} /> {result.standardTrucks} / {result.minimixTrucks}</strong></div>
                </div>
                <p className="calculator-fleet-note">{result.standardTrucks} {copy.standard} · {result.minimixTrucks} {copy.minimix}</p>
                {recommendedProduct && <p className="calculator-price">{formatProductPrice(recommendedProduct.price_per_m3)}<span>/m³</span></p>}
                {!recommendedProduct && <p className="calculator-unavailable">{copy.unavailable}</p>}
                <div className="calculator-actions">
                  <button type="button" className="btn btn-primary" disabled={!recommendedProduct} onClick={handleAddToCart}>
                    {copy.add} <Plus size={17} />
                  </button>
                  <Link to="/produk-layanan" className="calculator-catalog-link">{copy.catalog} <ArrowRight size={16} /></Link>
                </div>
                {notice && <p className="calculator-success"><CheckCircle2 size={16} /> {notice}</p>}
              </>
            )}
          </div>
        </div>
        <p className="calculator-disclaimer">{copy.disclaimer}</p>
      </div>
    </section>
  );
}
